import type { DashboardData, HealthRecord, NewHealthRecord, RecordKind } from "../types";
import { sessionExpiredEvent, sessionUser } from "../auth/session";

interface ErrorPayload {
  error?: string;
  code?: string;
  warnings?: { code: string }[];
}

export class ApiError extends Error {
  status: number;
  warnings: { code: string }[];
  code?: string;
  constructor(message: string, status: number, warnings: { code: string }[] = [], code?: string) {
    super(message);
    this.status = status;
    this.warnings = warnings;
    this.code = code;
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const owner = sessionUser();
  const response = await fetch(path, {
    credentials: "same-origin",
    ...init,
    headers: {
      Accept: "application/json",
      "X-Health-Request": "1",
      "X-Health-User": owner,
      ...init?.headers,
    },
  });

  if (owner !== sessionUser()) throw new DOMException("Identity changed", "AbortError");
  if (!response.ok) {
    if (response.status === 401 && typeof window !== "undefined") window.dispatchEvent(new Event(sessionExpiredEvent));
    let message = `Request failed with status ${response.status}`;
    let warnings: { code: string }[] = [];
    let code: string | undefined;
    try {
      const payload = (await response.json()) as ErrorPayload;
      if (payload.error) message = payload.error;
      if (payload.warnings) warnings = payload.warnings;
      if (typeof payload.code === "string") code = payload.code;
    } catch {
      // Keep the status-based message when a response has no JSON error body.
    }
    throw new ApiError(message, response.status, warnings, code);
  }

  if (response.status === 204) return undefined as T;
  const payload = (await response.json()) as T;
  if (owner !== sessionUser()) throw new DOMException("Identity changed", "AbortError");
  return payload;
}

export function fetchDashboard(): Promise<DashboardData> {
  return request<DashboardData>("/api/v1/dashboard");
}

export interface FetchRecordsOptions {
  kind?: RecordKind;
  from?: string;
  to?: string;
  timezone?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export function fetchRecords(options: FetchRecordsOptions = {}): Promise<HealthRecord[]> {
  return request<HealthRecord[]>(`/api/v1/records${recordsQuery(options)}`);
}

export async function fetchRecordCount(options: Omit<FetchRecordsOptions, "limit" | "offset"> = {}): Promise<number> {
  const result = await request<{ count: number }>(`/api/v1/records/count${recordsQuery(options)}`);
  return result.count;
}

function recordsQuery(options: FetchRecordsOptions): string {
  const query = new URLSearchParams();
  if (options.kind) query.set("kind", options.kind);
  if (options.from) query.set("from", options.from);
  if (options.to) query.set("to", options.to);
  if (options.timezone) query.set("timezone", options.timezone);
  if (options.search) query.set("q", options.search);
  if (options.limit) query.set("limit", String(options.limit));
  if (options.offset) query.set("offset", String(options.offset));
  return query.size > 0 ? `?${query.toString()}` : "";
}

export async function fetchAllRecords(options: Omit<FetchRecordsOptions, "limit" | "offset"> = {}): Promise<HealthRecord[]> {
  const pageSize = 5000;
  const records: HealthRecord[] = [];
  for (;;) {
    const page = await fetchRecords({ ...options, limit: pageSize, offset: records.length });
    records.push(...page);
    if (page.length < pageSize) return records;
  }
}

export function createRecord(record: NewHealthRecord): Promise<HealthRecord> {
  return request<HealthRecord>("/api/v1/records", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(record),
  });
}

export async function createRecords(records: NewHealthRecord[]): Promise<number> {
  const result = await request<{ created: number }>("/api/v1/records/bulk", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ records }),
  });
  return result.created;
}

export function updateRecord(id: string, record: NewHealthRecord): Promise<HealthRecord> {
  return request<HealthRecord>(`/api/v1/records/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(record),
  });
}

export function deleteRecord(id: string): Promise<void> {
  return request<void>(`/api/v1/records/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
